import { useEffect, useRef } from 'react';

/**
 * Drop-in <video> wrapper that pauses playback when the element is scrolled
 * out of view (≤10% visible). Saves mobile battery and bandwidth on feed
 * pages that autoplay short clips.
 *
 * Accepts all the standard <video> props.  Add `playOnVisible` to also
 * resume playback automatically when the video re-enters the viewport.
 */
export default function InViewVideo({
  playOnVisible = true,
  className = '',
  ...videoProps
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
          if (playOnVisible) {
            // Browsers may reject play() if the page hasn't been interacted
            // with yet — autoplay videos are muted so this almost always
            // succeeds.  We swallow the rejection silently.
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
        } else if (!el.paused) {
          el.pause();
        }
      },
      { threshold: [0, 0.1, 0.5] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [playOnVisible]);

  return <video ref={ref} className={className} {...videoProps} />;
}
